import {
  Article,
  Badge,
  Brush,
  CalendarToday,
  Campaign,
  Dialpad,
  Facebook,
  Groups,
  Image,
  Lock,
  NotificationsActive,
  Person,
  Place,
  RecordVoiceOver,
  Tune,
  Http,
  WhatsApp
} from "@material-ui/icons";
import { lazy } from "react";
import type { ComponentType, ReactNode } from "react";

import type { User } from "../../../@types/sharedSchema";

/**
 * THE SETTINGS REGISTRY — one row per settings section, and the only place a
 * section is described.
 *
 * ── Why a registry and not just JSX ──────────────────────────────────────────────
 *
 * A section has to render in two unrelated hosts: the settings page (a grouped accordion
 * — collapsed rows are the menu, the open row is the content) and `SettingsPanel`, the
 * side panel / full-screen modal opened from the feature page it belongs to. Before this
 * hosts were written independently every time, which is how the calendar ended up with
 * a bespoke `Drawer`, virtual numbers with a plain centred `Dialog` that was never
 * `fullScreen` on mobile, and location groups with a third variant — three
 * implementations of one idea, each with its own header markup and none of them
 * agreeing on mobile.
 *
 * With the registry a host takes an id and renders whatever the row says. Adding a
 * section, or surfacing an existing one somewhere new, is a row here plus a
 * `<SettingsPanel sectionId="…" />` at the call site — never a second copy of the
 * component.
 *
 * ── `Component` renders the BODY ONLY ────────────────────────────────────────────
 *
 * Titles come from `titleKey`/`descriptionKey` and are drawn by the host, because each
 * wants them in its own chrome (an accordion summary row vs. a panel header beside a
 * close button). A component that draws its own heading gets it twice. Every component
 * listed here has had its top-level heading removed for exactly that reason — and its
 * outer `<Card>`, since the accordion panel and the side panel are already the surface.
 * If you add a section, strip both.
 *
 * ── `isVisible` is the ONLY gate ─────────────────────────────────────────────────
 *
 * Gating used to live inside the components as an early `return null`, which is
 * invisible to a host: the page could not tell an empty section from a hidden one, so
 * the old page rendered nav entries for cards that drew nothing. Predicates live here
 * now, and the components trust their host. `SettingsPanel` re-checks before opening, so
 * a stale deep link to a section the user cannot see collapses to "nothing open" rather
 * than to an empty panel.
 */

export type SettingsSectionId =
  | "profile"
  | "security"
  | "appearance"
  | "minisite"
  | "media"
  | "realtorIdentity"
  | "listingAlerts"
  | "calendar"
  | "callTranscription"
  | "areas"
  | "orgPermissions"
  | "virtualNumbers"
  | "bulkMessaging"
  | "whatsappExtension"
  | "facebookLeads"
  | "madlan"
  | "metaPixel"
  | "webhook";

export type SettingsGroupId =
  | "brand"
  | "notifications"
  | "team"
  | "channels"
  | "integrations"
  | "account";

/**
 * What a gate may ask about. Deliberately a plain snapshot rather than the live
 * contexts: `getVisibleSettingsSections` is called from the nav, from the router and
 * from `SettingsPanel`, and a predicate that called hooks would tie all three to one
 * provider tree — including `SettingsPanel`, which has call sites inside dialogs.
 */
export type SettingsVisibilityContext = {
  userSettings: User | null | undefined;
  /**
   * The user has at least one virtual number to manage — from `userSettings`, never from
   * `useVirtualNumbers`. See the warning on `useSettingsVisibility`: every host resolves
   * gates on mount, so a gate that fetches makes a CLOSED panel cost a request.
   */
  hasVirtualNumbers: boolean;
  /** The user belongs to at least one agency, so agency permissions mean something. */
  hasOrganization: boolean;
};

export type SettingsSection = {
  id: SettingsSectionId;
  groupId: SettingsGroupId;
  /** i18n key for the section name — used by the nav, the page header and the panel header. */
  titleKey: string;
  /** i18n key for the one-line explanation under the name. */
  descriptionKey: string;
  icon: ReactNode;
  /** Body only — no heading. See the file docblock. */
  Component: ComponentType;
  isVisible: (context: SettingsVisibilityContext) => boolean;
  /**
   * Extra words the search matches on — LITERAL strings, in Hebrew AND English, in the
   * same array.
   *
   * These are match tokens, not UI text, which is why they are hardcoded rather than i18n
   * keys. Putting them through `t()` would resolve them in the ACTIVE language only, and
   * a realtor on the Hebrew UI typing "minisite" (or a tester on the English UI typing
   * "מיניסייט") would then find nothing — the exact failure this array exists to prevent.
   * `matchesSettingsQuery` searches both locales of `titleKey`/`descriptionKey` too, so
   * what belongs here is only what NO label says: synonyms, common misspellings
   * ("סיסמא" without the ה, "ווטסאפ" with one ו), product names and the words people use
   * for a thing when they don't know ours ("SMS", "ריבית", "טפט").
   */
  searchTags?: string[];
};

/**
 * Render order. Identity and branding lead because that is what a realtor opens settings
 * to change; `account` is last because a password and a theme are things you set once.
 *
 * There is no `plan` group any more — the products card was a read-only summary of what
 * the account includes, which belongs on a billing surface rather than among controls that
 * do something when you touch them.
 */
export const SETTINGS_GROUPS: { id: SettingsGroupId; titleKey: string }[] = [
  { id: "brand", titleKey: "settingsPage.groups.brand" },
  { id: "notifications", titleKey: "settingsPage.groups.notifications" },
  { id: "team", titleKey: "settingsPage.groups.team" },
  { id: "channels", titleKey: "settingsPage.groups.channels" },
  { id: "integrations", titleKey: "settingsPage.groups.integrations" },
  { id: "account", titleKey: "settingsPage.groups.account" }
];

const isRealtor = ({ userSettings }: SettingsVisibilityContext): boolean =>
  userSettings?.crmType === "realEstate";

const hasFeature =
  (feature: string) =>
  ({ userSettings }: SettingsVisibilityContext): boolean =>
    userSettings?.advancedFeatures?.includes(feature as never) ?? false;

/**
 * Every section is `React.lazy`, and that is a bundle decision rather than a style one.
 *
 * The registry is imported by `SettingsPanel`, which is mounted on the calendar page, the
 * calls pages, the CRM workspace and inside the bulk-message dialog. Static imports here
 * would pull all nineteen section components — `MinisiteThemeSettings` alone is ~690
 * lines, plus the whole CRM org screen — into each of those chunks, to render one panel.
 *
 * Hosts must therefore render sections inside a `<Suspense>`; both of ours do
 * (`SettingsSectionView`). Named exports are re-shaped to a default here because `lazy`
 * only accepts a module whose `default` is the component.
 */
const section = (load: () => Promise<Record<string, unknown>>, name = "default"): ComponentType =>
  lazy(async () => {
    const mod = await load();
    return { default: (mod[name] ?? mod.default) as ComponentType };
  });

export const SETTINGS_SECTIONS: SettingsSection[] = [
  {
    id: "profile",
    // Branding, not "account": the name, phone and agency here are what a client sees on
    // the minisite, on creatives and on documents — the same audience as the rows beside
    // it. What is left in `account` is only what nobody else ever sees.
    groupId: "brand",
    titleKey: "settingsPage.sections.profile.title",
    descriptionKey: "settingsPage.sections.profile.description",
    icon: <Person />,
    Component: section(() => import("./ProfileCard")),
    isVisible: () => true,
    searchTags: [
      "שם", "טלפון", "נייד", "פלאפון", "חברה", "משרד", "ח.פ", "חפ", "עוסק", "רישיון", "רשיון",
      "תיווך", "תמונת פרופיל", "name", "phone", "mobile", "company", "agency", "license", "vat",
      "avatar", "profile picture", "contact details"
    ]
  },
  {
    id: "security",
    groupId: "account",
    titleKey: "settingsPage.sections.security.title",
    descriptionKey: "settingsPage.sections.security.description",
    icon: <Lock />,
    Component: section(() => import("./SecuritySettings")),
    isVisible: () => true,
    searchTags: [
      "סיסמה", "סיסמא", "ססמה", "אבטחה", "התחברות", "כניסה", "גוגל", "איפוס סיסמה", "שכחתי סיסמה",
      "password", "passcode", "security", "login", "sign in", "google", "reset password",
      "forgot password", "credentials"
    ]
  },
  {
    id: "appearance",
    groupId: "account",
    titleKey: "settingsPage.sections.appearance.title",
    descriptionKey: "settingsPage.sections.appearance.description",
    icon: <Tune />,
    Component: section(() => import("./AppearanceSettings"), "AppearanceSettings"),
    isVisible: () => true,
    searchTags: [
      "מראה", "עיצוב", "כהה", "בהיר", "לילה", "ערכת נושא", "רוחב", "מסך מלא", "שפה", "appearance",
      "theme", "dark mode", "light mode", "night", "stretch", "width", "fullscreen", "display"
    ]
  },

  {
    id: "minisite",
    groupId: "brand",
    titleKey: "settingsPage.sections.minisite.title",
    descriptionKey: "settingsPage.sections.minisite.description",
    icon: <Brush />,
    Component: section(() => import("./sections/MinisiteSettings"), "MinisiteSettings"),
    isVisible: isRealtor,
    searchTags: [
      "מיניסייט", "מיני סייט", "אתר", "דף נחיתה", "צבע", "צבעים", "רקע", "טפט", "אודות", "ביו",
      "כתובת", "כינוי", "קישור", "לינק", "שם", "תמונה", "שם מקוצר",
      "משפט תדמית", "סלוגן", "אינסטגרם", "פייסבוק", "כתובת המשרד", "minisite", "mini site",
      "website", "landing page", "color", "colours", "background", "hero", "about", "bio",
      "tagline", "slogan", "instagram", "facebook", "branding",
      // The identity half — alias, name and photo moved in here from `EditProfileDialog`.
      "alias", "handle", "url", "link", "address", "name", "photo", "avatar", "profile"
    ]
  },
  {
    id: "media",
    groupId: "brand",
    titleKey: "settingsPage.sections.media.title",
    descriptionKey: "settingsPage.sections.media.description",
    icon: <Image />,
    Component: section(() => import("./UserImageAndLogoSettings"), "UserImageAndLogoSettings"),
    isVisible: isRealtor,
    searchTags: [
      "תמונה", "תמונת משתמש", "לוגו", "חתימה", "חתימה דיגיטלית", "מסמכים", "קריאייטיב", "קריאטיב",
      "photo", "image", "picture", "logo", "signature", "sign", "documents", "creative",
      "branding"
    ]
  },
  {
    id: "realtorIdentity",
    groupId: "brand",
    titleKey: "settingsPage.sections.realtorIdentity.title",
    descriptionKey: "settingsPage.sections.realtorIdentity.description",
    icon: <Badge />,
    Component: section(() => import("./RealtorIdentitySettings"), "RealtorIdentitySettings"),
    isVisible: isRealtor,
    searchTags: [
      "יד2", "יד 2", "מדלן", "שם מתווך", "שם המשרד", "זהות", "סוכן", "yad2", "madlan",
      "realtor name", "agent name", "agency name", "identity"
    ]
  },

  {
    id: "listingAlerts",
    groupId: "notifications",
    titleKey: "settingsPage.sections.listingAlerts.title",
    descriptionKey: "settingsPage.sections.listingAlerts.description",
    icon: <NotificationsActive />,
    Component: section(() => import("./sections/ListingAlertsSettings"), "ListingAlertsSettings"),
    isVisible: isRealtor,
    searchTags: [
      "התראות", "התראה", "נכסים", "נכסים חדשים", "ירידת מחיר", "הורדת מחיר", "אזורים",
      "אזורי עניין", "מעוניין באזורים", "בלעדיות", "מכירה", "השכרה", "וואטסאפ", "ווטסאפ", "פוש",
      "alerts", "notifications", "listings", "new listings", "price drop", "price reduction",
      "areas", "neighborhoods", "exclusivity", "sale", "rent", "whatsapp", "push"
    ]
  },
  {
    id: "calendar",
    groupId: "notifications",
    titleKey: "settingsPage.sections.calendar.title",
    descriptionKey: "settingsPage.sections.calendar.description",
    icon: <CalendarToday />,
    Component: section(() => import("./sections/CalendarRemindersSettings"), "CalendarRemindersSettings"),
    isVisible: () => true,
    searchTags: [
      "יומן", "לוח שנה", "תזכורות", "תזכורת", "פגישות", "פגישה", "משימות", "משימה", "גוגל קלנדר",
      "סנכרון", "סינכרון", "calendar", "reminders", "reminder", "meetings", "events", "tasks",
      "google calendar", "sync", "appointments"
    ]
  },
  {
    id: "callTranscription",
    groupId: "notifications",
    titleKey: "settingsPage.sections.callTranscription.title",
    descriptionKey: "settingsPage.sections.callTranscription.description",
    icon: <RecordVoiceOver />,
    Component: section(() => import("./sections/CallTranscriptionSettings"), "CallTranscriptionSettings"),
    isVisible: isRealtor,
    searchTags: [
      "תמלול", "שיחות", "הקלטות", "הקלטה", "שפה", "שפות", "עברית", "אנגלית", "ערבית", "רוסית",
      "transcription", "transcribe", "calls", "recordings", "language", "languages", "hebrew",
      "english", "arabic", "russian"
    ]
  },

  {
    id: "areas",
    groupId: "team",
    titleKey: "settingsPage.sections.areas.title",
    descriptionKey: "settingsPage.sections.areas.description",
    icon: <Place />,
    Component: section(() => import("./LocationGroupsSettings"), "LocationGroupsSettings"),
    isVisible: isRealtor,
    searchTags: [
      "קבוצות", "קבוצות אזורים", "אזורים", "שכונות", "ערים", "רשימת אזורים", "groups",
      "area groups", "areas", "neighborhoods", "cities", "location groups", "shortcut"
    ]
  },
  {
    id: "orgPermissions",
    groupId: "team",
    titleKey: "settingsPage.sections.orgPermissions.title",
    descriptionKey: "settingsPage.sections.orgPermissions.description",
    icon: <Groups />,
    // The CRM agency screen is written for both audiences — an admin edits the roster,
    // a member reads their own permissions — so it is NOT gated on adminship here. See
    // its own docblock; a client-side admin guard would hide the read-only view from
    // the people it was written for.
    Component: section(() => import("../../_dashboard/crm/org/CrmOrgSettings")),
    isVisible: ({ hasOrganization, ...rest }) => hasOrganization && hasFeature("crm")({ hasOrganization, ...rest }),
    searchTags: [
      "הרשאות", "הרשאה", "משרד", "סוכנות", "צוות", "חברי צוות", "מנהל", "סוכנים", "מי רואה",
      "מאגר", "שיוך", "permissions", "roles", "agency", "office", "team", "members", "admin",
      "agents", "pool", "access", "visibility"
    ]
  },

  {
    id: "virtualNumbers",
    groupId: "channels",
    titleKey: "settingsPage.sections.virtualNumbers.title",
    descriptionKey: "settingsPage.sections.virtualNumbers.description",
    icon: <Dialpad />,
    Component: section(() => import("../VirtualNumbers/VirtualNumbersSettings"), "VirtualNumbersSettings"),
    isVisible: ({ hasVirtualNumbers }) => hasVirtualNumbers,
    searchTags: [
      "מספרים", "מספר וירטואלי", "מספרים וירטואליים", "מספר", "תיוג", "תגית", "מקור ליד", "שלוחה",
      "בוט", "numbers", "virtual numbers", "phone numbers", "tag", "lead source",
      "tracking number", "did"
    ]
  },
  {
    id: "bulkMessaging",
    groupId: "channels",
    titleKey: "settingsPage.sections.bulkMessaging.title",
    descriptionKey: "settingsPage.sections.bulkMessaging.description",
    icon: <Campaign />,
    Component: section(() => import("./sections/BulkMessagingSettings"), "BulkMessagingSettings"),
    isVisible: () => true,
    searchTags: [
      "דיוור", "הודעות המוניות", "תפוצה", "קצב", "השהיה", "המתנה", "הפסקה", "חסימה", "וואטסאפ",
      "ווטסאפ", "קמפיין", "sms", "bulk", "bulk messaging", "broadcast", "mass message", "delay",
      "pace", "throttle", "cooldown", "cool down", "ban", "block", "whatsapp", "campaign"
    ]
  },
  {
    id: "whatsappExtension",
    groupId: "channels",
    titleKey: "settingsPage.sections.whatsappExtension.title",
    descriptionKey: "settingsPage.sections.whatsappExtension.description",
    icon: <WhatsApp />,
    Component: section(() => import("./Integrations"), "Integrations"),
    isVisible: () => true,
    searchTags: [
      "וואטסאפ", "ווטסאפ", "תוסף", "הרחבה", "אקסטנשן", "כרום", "ייבוא", "ייבוא אנשי קשר", "סנכרון",
      "whatsapp", "extension", "chrome", "plugin", "add-on", "import", "import contacts", "sync"
    ]
  },
  {
    id: "facebookLeads",
    groupId: "channels",
    titleKey: "settingsPage.sections.facebookLeads.title",
    descriptionKey: "settingsPage.sections.facebookLeads.description",
    icon: <Facebook />,
    Component: section(() => import("./FacebookLeadsSettings"), "FacebookLeadsSettings"),
    isVisible: hasFeature("facebookLeads"),
    searchTags: [
      "פייסבוק", "פייסבוק לידים", "קבוצות", "לידים", "ליד", "סריקה", "מודעות", "פוסטים",
      "facebook", "fb", "leads", "groups", "scraping", "scan", "posts", "meta"
    ]
  },

  {
    id: "madlan",
    groupId: "integrations",
    titleKey: "settingsPage.sections.madlan.title",
    descriptionKey: "settingsPage.sections.madlan.description",
    icon: <Article />,
    Component: section(() => import("./sections/MadlanIntegrationSettings"), "MadlanIntegrationSettings"),
    isVisible: isRealtor,
    searchTags: [
      "מדלן", "פרסום", "העלאת נכס", "אינטגרציה", "חיבור", "סיסמת מדלן", "משתמש מדלן", "madlan",
      "publish", "publishing", "integration", "connect", "credentials", "listing upload"
    ]
  },
  {
    id: "metaPixel",
    groupId: "integrations",
    titleKey: "settingsPage.sections.metaPixel.title",
    descriptionKey: "settingsPage.sections.metaPixel.description",
    icon: <Facebook />,
    Component: section(() => import("./sections/MetaPixelSettings"), "MetaPixelSettings"),
    isVisible: isRealtor,
    searchTags: [
      "פיקסל", "מטא", "פייסבוק", "מעקב", "רימרקטינג", "ריטרגטינג", "פרסום", "אנליטיקס", "pixel",
      "meta", "facebook", "tracking", "remarketing", "retargeting", "ads", "analytics",
      "conversion"
    ]
  },
  {
    id: "webhook",
    groupId: "integrations",
    titleKey: "settingsPage.sections.webhook.title",
    descriptionKey: "settingsPage.sections.webhook.description",
    icon: <Http />,
    Component: section(() => import("./WebhookIntegrationCard"), "WebhookIntegrationCard"),
    isVisible: isRealtor,
    searchTags: [
      "וובהוק", "ובהוק", "אינטגרציה", "חיבור", "מערכת חיצונית", "אוטומציה", "זאפייר", "webhook",
      "api", "integration", "endpoint", "automation", "zapier", "make", "external system"
    ]
  }
];

const SECTIONS_BY_ID = new Map(SETTINGS_SECTIONS.map((entry) => [entry.id, entry]));

/** The locales `i18n.ts` registers as resources — both are bundled, so `t(key, { lng })` is sync. */
const SEARCHABLE_LOCALES = ["he", "en"] as const;

/**
 * Does this section match what the user typed?
 *
 * ⚠️ SEARCHES BOTH LOCALES, ALWAYS — not the active one.
 *
 * The app is Hebrew for essentially every customer, but the vocabulary is not: realtors
 * type "webhook", "pixel", "whatsapp" and "yad2" in Latin letters because that is what
 * those things are called, and they are looking at a UI that says "Webhook" and
 * "Meta Pixel" in Latin letters too. A single-language search resolves `titleKey` to the
 * Hebrew label and then fails to match the very word printed on the row.
 *
 * So each section's title and description are resolved in `he` AND `en` and both go into
 * the haystack, on top of the literal bilingual `searchTags`. Both locale files are
 * `import`ed into `i18n.ts` as resources, so asking for the other language costs a map
 * lookup and never a fetch.
 *
 * `translate` is passed in rather than imported so this stays a pure function — the nav
 * hands it `t` from `useTranslation`, and a test can hand it anything.
 */
export function matchesSettingsQuery(
  entry: SettingsSection,
  query: string,
  translate: (key: string, options?: { lng: string }) => string
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  const haystack = [
    ...SEARCHABLE_LOCALES.flatMap((lng) => [
      translate(entry.titleKey, { lng }),
      translate(entry.descriptionKey, { lng })
    ]),
    ...(entry.searchTags ?? [])
  ]
    .join(" ")
    .toLowerCase();

  // Every whitespace-separated word must appear somewhere, so "מיניסייט צבע" narrows
  // rather than widening the way a plain substring test on the raw string would.
  return needle.split(/\s+/).every((word) => haystack.includes(word));
}

export function getSettingsSection(id: SettingsSectionId): SettingsSection | undefined {
  return SECTIONS_BY_ID.get(id);
}

export function isSettingsSectionId(value: unknown): value is SettingsSectionId {
  return typeof value === "string" && SECTIONS_BY_ID.has(value as SettingsSectionId);
}

export function getVisibleSettingsSections(context: SettingsVisibilityContext): SettingsSection[] {
  return SETTINGS_SECTIONS.filter((entry) => entry.isVisible(context));
}

/**
 * The grouped shape the nav renders, with empty groups dropped — a heading over
 * nothing is what the old page did with `RealtorSettings` for a generic account.
 */
export function getVisibleSettingsGroups(
  context: SettingsVisibilityContext
): { id: SettingsGroupId; titleKey: string; sections: SettingsSection[] }[] {
  const visible = getVisibleSettingsSections(context);

  return SETTINGS_GROUPS.map((group) => ({
    ...group,
    sections: visible.filter((entry) => entry.groupId === group.id)
  })).filter((group) => group.sections.length > 0);
}
